import {
  InlineNotification,
  Modal,
  RadioButton,
  TextInput,
  Layer,
} from '@carbon/react';
import { capitalize } from 'lodash-es';
import styles from './save-config-modal.module.scss';
import { useState } from 'react';

interface SaveConfigModalProps {
  section: 'general' | 'segmentation' | 'outlier';
  config?: string;
  onClose: () => void;
  disableOptions?: boolean;
  defaultOption?: 'existing' | 'new';
  error?: string | null;
  onSave: (name: string, isNew: boolean) => void;
}

export const SaveConfigModal = ({
  section,
  config,
  onClose,
  disableOptions = false,
  defaultOption,
  error,
  onSave,
}: SaveConfigModalProps) => {
  const [configName, setConfigName] = useState('');
  const [saveOption, setSaveOption] = useState<'existing' | 'new' | undefined>(
    defaultOption
  );

  return (
    <Modal
      open={true}
      size="md"
      modalHeading={`Save ${capitalize(section)} Config`}
      className={styles.saveConfigModal}
      secondaryButtonText="Cancel"
      primaryButtonText="Save"
      onRequestClose={onClose}
      onRequestSubmit={() => onSave(configName, saveOption === 'new')}
      onSecondarySubmit={onClose}
      primaryButtonDisabled={!configName || !saveOption}
      preventCloseOnClickOutside
      closeButtonLabel="Close"
      aria-label="Save config"
    >
      {!disableOptions && (
        <>
          <p className={styles.description}>
            {`Editing ${config} ${capitalize(
              section
            )} record might affect other DCA Scenarios. How would you like to proceed?`}
          </p>

          <div className={styles.saveConfigOptionsContainer}>
            <div className={styles.radioButtonContainer}>
              <RadioButton
                id="existing"
                labelText="Save the updates for Existing Config"
                value="existing"
                checked={saveOption === 'existing'}
                onChange={() => {
                  setSaveOption('existing');

                  setConfigName(config ?? '');
                }}
              />
              <p className={styles.radioButtonDescription}>
                Select this option to save the existing Config with new updates.
              </p>
            </div>
            <div className={styles.radioButtonContainer}>
              <RadioButton
                id="new"
                labelText="Save as New Config"
                value="new"
                checked={saveOption === 'new'}
                onChange={() => {
                  setSaveOption('new');
                  setConfigName('');
                }}
              />
              <p className={styles.radioButtonDescription}>
                Select this option to save as new Config.
              </p>
            </div>
          </div>
        </>
      )}
      <div className={styles.configNameContainer}>
        <Layer>
          <TextInput
            id="config-name"
            labelText="Add Name for New Config* (required)"
            placeholder={`New ${capitalize(section)} Config`}
            size="lg"
            value={configName}
            disabled={saveOption === 'existing'}
            onChange={(e) => setConfigName(e.target.value)}
          />
        </Layer>
      </div>
      {error && (
        <InlineNotification
          className={styles.inlineNotification}
          // className={styles.errorNotification}
          kind="error"
          title="Error"
          subtitle={error}
          lowContrast
          hideCloseButton
        />
      )}
    </Modal>
  );
};
